import React from 'react';
import { format } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';

import { fixDecimal } from 'utils/general';
import { currency } from 'globals/currency';

export interface ColumnOptions {
  customHeader?: string;
  customCellFormatter?: (props: TableCellProps<any>) => React.ReactNode;
  shouldFixDecimal?: boolean;
  filterType?: 'text' | 'select' | 'none';
}

export const generateColumn = (
  fieldName: string,
  options: ColumnOptions = {}
): ColumnDef<unknown, unknown> => {
  const {
    customHeader,
    customCellFormatter,
    filterType = 'text',
    shouldFixDecimal = false,
  } = options;

  const baseColumn: ColumnDef<unknown, unknown> = {
    header: customHeader || fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
    accessorKey: fieldName,
    enableColumnFilter: filterType !== 'none',
    cell: (props) => props.getValue(),
    meta: filterType !== 'none' ? { filterVariant: filterType } : undefined,
  };

  // Apply custom cell formatter or decimal formatting
  if (customCellFormatter) {
    baseColumn.cell = customCellFormatter;
  } else if (shouldFixDecimal) {
    baseColumn.cell = (props) => (
      <div>
        {fixDecimal(props.getValue())} {currency}
      </div>
    );
  }

  return baseColumn;
};

export interface TableCellProps<Type = unknown> {
  getValue: () => any;
  row: {
    original: Type;
  };
}

export function getRTOriginal<Type>(props: TableCellProps<Type>): Type {
  return props.row.original;
}

export function TableDateFormatHelper<T>(
  field: keyof T,
  desiredFormat: string = 'yyyy-MM-dd HH:mm'
) {
  return (props: TableCellProps<T>) => {
    const data = getRTOriginal<T>(props);
    const dateValue = data[field];
    if (!dateValue) {
      return '-';
    }
    return format(
      dateValue instanceof Date ? dateValue : new Date(dateValue as unknown as string),
      desiredFormat
    );
  };
}
